import React, { useState } from 'react';
import { ChevronRightIcon, CheckIcon } from '@radix-ui/react-icons';
import { ROW_INSET_X } from 'modules/Common/ui/CgFilterDropdown';

const groupKey = (group, index) => group.key ?? group.label ?? String(index);

export const filterTree = (groups, query, fn) => {
  const q = String(query ?? '').trim();
  if (!q) return groups.filter((g) => g.options.length > 0);
  const lowered = q.toLowerCase();
  return groups
    .map((g) =>
      String(g.label ?? '')
        .toLowerCase()
        .includes(lowered)
        ? g
        : {
            ...g,
            options: g.options.filter((o) => fn({ label: o.label, value: o.value, data: o }, q)),
          },
    )
    .filter((g) => g.options.length > 0);
};

const rowsOf = (container) => Array.from(container.querySelectorAll('[data-tree-row]'));

const CategoryBody = ({ group, count, isOpen }) => (
  <>
    <ChevronRightIcon
      className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
      aria-hidden
    />
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="text-sm font-medium text-slate-800">{group.label}</span>
      {group.description ? (
        <span className="text-xs leading-4 text-slate-500">{group.description}</span>
      ) : null}
    </span>
    {count ? (
      <span className="mt-0.5 shrink-0 text-xs tabular-nums text-slate-400">{count}</span>
    ) : null}
  </>
);

const leafClass = (o, isSelected) =>
  `w-full flex items-start justify-between gap-2 ${ROW_INSET_X} py-2 text-sm rounded-md text-left focus:outline-none ${
    isSelected
      ? 'bg-primaryColor/10 text-primaryColor font-medium hover:bg-primaryColor/15 focus:bg-primaryColor/15'
      : `${o.isAction ? 'text-primaryColor font-medium' : 'text-slate-700'} hover:bg-primaryColor/5 focus:bg-primaryColor/5`
  }`;

export const TreeList = ({ groups, searching, value, onPick, renderOption, onExitTop, id }) => {
  const [expanded, setExpanded] = useState(() => {
    const index = groups.findIndex((g) => g.options.some((o) => o.value === value));
    return new Set(index >= 0 ? [groupKey(groups[index], index)] : []);
  });

  const setOpen = (key, open) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });

  const handleKeyDown = (e) => {
    const row = e.target.closest?.('[data-tree-row]');
    if (!row) return;
    const rows = rowsOf(e.currentTarget);
    const at = rows.indexOf(row);
    const branch = row.dataset.branch;
    const parent = row.dataset.parent;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      rows[at + 1]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (at <= 0) onExitTop?.();
      else rows[at - 1].focus();
    } else if (e.key === 'ArrowRight' && branch !== undefined) {
      e.preventDefault();
      if (row.getAttribute('aria-expanded') === 'true') {
        const next = rows[at + 1];
        if (next?.dataset.parent === branch) next.focus();
      } else {
        setOpen(branch, true);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (parent !== undefined) {
        Array.from(e.currentTarget.querySelectorAll('[data-branch]'))
          .find((el) => el.dataset.branch === parent)
          ?.focus();
      } else if (branch !== undefined && row.getAttribute('aria-expanded') === 'true') {
        setOpen(branch, false);
      }
    }
  };

  return (
    <div
      id={id}
      role="tree"
      onKeyDown={handleKeyDown}
    >
      {groups.map((group, index) => {
        const key = groupKey(group, index);
        const isOpen = searching || expanded.has(key);
        const count = group.options.filter((o) => !o.isAction).length;
        return (
          <div key={key}>
            {searching ? (
              <div
                className={`w-full flex items-start gap-2 ${ROW_INSET_X} py-2 rounded-md text-left`}
              >
                <CategoryBody
                  group={group}
                  count={count}
                  isOpen
                />
              </div>
            ) : (
              <button
                type="button"
                role="treeitem"
                aria-level={1}
                aria-expanded={isOpen}
                data-tree-row=""
                data-branch={key}
                onClick={() => setOpen(key, !isOpen)}
                className={`w-full flex items-start gap-2 ${ROW_INSET_X} py-2 rounded-md text-left hover:bg-slate-50 focus:bg-slate-50 focus:outline-none`}
              >
                <CategoryBody
                  group={group}
                  count={count}
                  isOpen={isOpen}
                />
              </button>
            )}
            {isOpen ? (
              <div
                role="group"
                className="ml-[19px] border-l border-slate-200 pl-1.5"
              >
                {group.options.map((o) => {
                  const isSelected = o.value === value;
                  const shown = { ...o, label: o.treeLabel ?? o.label };
                  return (
                    <button
                      key={o.value}
                      type="button"
                      role="treeitem"
                      aria-level={2}
                      aria-selected={isSelected}
                      data-tree-row=""
                      data-parent={key}
                      disabled={o.isDisabled}
                      onClick={() => onPick(o.value)}
                      className={leafClass(o, isSelected)}
                    >
                      <span className="flex-1 break-words">
                        {renderOption ? renderOption(shown) : shown.label}
                      </span>
                      {isSelected && <CheckIcon className="text-primaryColor shrink-0 mt-0.5" />}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
